import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Code2Icon, XIcon, PaletteIcon, ImageIcon, LayoutIcon, DownloadIcon, ChevronDownIcon } from 'lucide-react';
import { StyleGuideTab } from './StyleGuideTab';
import { AssetsTab } from './AssetsTab';
import { PagesTab } from './PagesTab';
import { CodeExportsTab } from './CodeExportsTab';
export function DevModePanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('style');
  const [showExportMenu, setShowExportMenu] = useState(false);
  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const tabs = [{
    id: 'style',
    label: 'Style Guide',
    icon: <PaletteIcon className="w-4 h-4" data-id="element-144" />
  }, {
    id: 'assets',
    label: 'Assets',
    icon: <ImageIcon className="w-4 h-4" data-id="element-145" />
  }, {
    id: 'pages',
    label: 'Pages',
    icon: <LayoutIcon className="w-4 h-4" data-id="element-146" />
  }, {
    id: 'code',
    label: 'Code',
    icon: <Code2Icon className="w-4 h-4" data-id="element-147" />
  }];
  return <>
      {/* Toggle Button */}
      <button onClick={() => setIsOpen(true)} className={`fixed top-4 left-4 z-50 flex items-center gap-2 px-3 py-1.5 bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white rounded-full border border-slate-700 shadow-lg backdrop-blur-sm transition-all duration-300 font-mono text-xs font-medium ${isOpen ? 'opacity-0 pointer-events-none translate-y-[-20px]' : 'opacity-100 translate-y-0'}`} title="Toggle Dev Mode (Cmd/Ctrl + D)" data-id="element-148">
        <Code2Icon className="w-3.5 h-3.5 text-blue-400" data-id="element-149" />
        <span data-id="element-150">Dev Mode</span>
      </button>

      {/* Backdrop */}
      <AnimatePresence data-id="element-151">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setIsOpen(false)} className="fixed inset-0 bg-slate-950/20 backdrop-blur-[1px] z-[9998]" data-id="element-152" />}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence data-id="element-153">
        {isOpen && <motion.div initial={{
        x: '100%',
        boxShadow: '-10px 0 30px rgba(0,0,0,0)'
      }} animate={{
        x: 0,
        boxShadow: '-10px 0 30px rgba(0,0,0,0.3)'
      }} exit={{
        x: '100%',
        boxShadow: '-10px 0 30px rgba(0,0,0,0)'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 bottom-0 w-[320px] bg-[#0f172a] border-l border-slate-800 z-[9999] flex flex-col font-sans text-slate-200 overflow-hidden" data-id="element-154">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900/50" data-id="element-155">
              <div className="flex items-center gap-2" data-id="element-156">
                <div className="p-1.5 bg-blue-500/10 rounded border border-blue-500/20" data-id="element-157">
                  <Code2Icon className="w-4 h-4 text-blue-400" data-id="element-158" />
                </div>
                <span className="font-semibold text-sm tracking-wide text-white" data-id="element-159">
                  Dev Mode
                </span>
              </div>

              <div className="flex items-center gap-2" data-id="element-160">
                {/* Quick Export Dropdown */}
                <div className="relative" data-id="element-161">
                  <button onClick={() => setShowExportMenu(!showExportMenu)} className="flex items-center gap-1.5 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 rounded border border-slate-700 text-xs font-medium transition-colors" data-id="element-162">
                    <DownloadIcon className="w-3 h-3" data-id="element-163" />
                    <span data-id="element-164">Export</span>
                    <ChevronDownIcon className="w-3 h-3 text-slate-400" data-id="element-165" />
                  </button>

                  <AnimatePresence data-id="element-166">
                    {showExportMenu && <motion.div initial={{
                  opacity: 0,
                  y: 5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 5
                }} className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-slate-700 rounded-md shadow-xl overflow-hidden z-50" data-id="element-167">
                        <div className="py-1" data-id="element-168">
                          <button onClick={() => {
                      setActiveTab('assets');
                      setShowExportMenu(false);
                    }} className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors" data-id="element-169">
                            Browse Assets
                          </button>
                          <button onClick={() => {
                      setActiveTab('code');
                      setShowExportMenu(false);
                    }} className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors" data-id="element-170">
                            CSS & JS Files
                          </button>
                          <button onClick={() => {
                      setActiveTab('style');
                      setShowExportMenu(false);
                    }} className="w-full text-left px-3 py-2 text-xs text-slate-300 hover:bg-slate-700 hover:text-white transition-colors" data-id="element-171">
                            Style Guide CSS
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                <button onClick={() => setIsOpen(false)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors" data-id="element-172">
                  <XIcon className="w-4 h-4" data-id="element-173" />
                </button>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="flex border-b border-slate-800 bg-slate-900/30 flex-shrink-0" data-id="element-174">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 flex flex-col items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors relative ${activeTab === tab.id ? 'text-blue-400' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'}`} data-id="element-175">
                  {tab.icon}
                  <span data-id="element-176">{tab.label}</span>
                  {activeTab === tab.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-177" />}
                </button>)}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto relative bg-slate-950/50" data-id="element-178">
              <AnimatePresence mode="wait" data-id="element-179">
                <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.15
            }} className="h-full" data-id="element-180">
                  {activeTab === 'style' && <StyleGuideTab data-id="element-181" />}
                  {activeTab === 'assets' && <AssetsTab data-id="element-182" />}
                  {activeTab === 'pages' && <PagesTab data-id="element-183" />}
                  {activeTab === 'code' && <CodeExportsTab data-id="element-184" />}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}